import React, { useEffect, useState } from 'react';
import { useToast } from '../components/Toast';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const KakaoLogin = () => {
  const toast = useToast();
  const [isKakaoLoaded, setIsKakaoLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [testNickname, setTestNickname] = useState('');

  useEffect(() => {
    // 카카오 SDK 로드
    const script = document.createElement('script');
    script.src = 'https://developers.kakao.com/sdk/js/kakao.js';
    script.async = true;
    script.onload = () => {
      if (window.Kakao) {
        window.Kakao.init('dca70edc344901646964578723caa2ea');
        setIsKakaoLoaded(true);
      }
    };
    document.head.appendChild(script);
  }, []);

  // 401 리다이렉트(apiFetch)로 온 경우 만료 안내 배너 표시
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === '1') {
      setError('세션이 만료되었습니다. 다시 로그인해주세요.');
    }
  }, []);

  // 로그인 성공 공통 처리 — 카카오/테스트 로그인이 공유
  const handleLoginSuccess = (data) => {
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('user', JSON.stringify(data.userInfo));

    // 커플 정보 저장 (있는 경우) — 매칭 대기(1인) 커플은 커플이 아니라 대기 상태로 저장
    if (data.coupleInfo && data.coupleInfo.matched) {
      localStorage.setItem('coupleInfo', JSON.stringify(data.coupleInfo));
      localStorage.removeItem('pendingInvite');
    } else if (data.coupleInfo) {
      localStorage.setItem('pendingInvite', JSON.stringify({
        inviteCode: data.coupleInfo.inviteCode,
        expiresAt: data.coupleInfo.inviteExpiresAt
      }));
      localStorage.removeItem('coupleInfo');
    } else {
      localStorage.removeItem('coupleInfo');
      localStorage.removeItem('pendingInvite');
    }

    window.location.href = '/home';
  };

  // 백엔드로 액세스 토큰만 전송한다.
  // 이메일·닉네임·프사를 함께 보내면 서버가 클라이언트 값으로 신원을 판단하게 되어 계정 탈취가 가능하다.
  // 서버가 이 토큰으로 카카오에 직접 조회해 신원을 확정한다.
  const sendUserInfoToBackend = async (accessToken) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/kakao`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessToken })
      });

      if (response.ok) {
        const res = await response.json();
        handleLoginSuccess(res.data);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.errorMessage || errorData.message || '백엔드 인증 실패');
      }
    } catch (error) {
      console.error('백엔드 통신 오류:', error);
      setError('로그인 처리 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };
    
  // 카카오 로그인 처리 함수
  const handleKakaoLogin = () => {
    if (!window.Kakao || !isKakaoLoaded) {
      toast.error('카카오 SDK가 로드되지 않았습니다.');
      return;
    }

    setIsLoading(true);
    setError('');

    window.Kakao.Auth.login({
      // 카카오톡 앱 로그인 시도를 건너뛴다 — 데스크톱에는 intent 스킴 핸들러가 없어
      // 콘솔에 실행 실패 에러가 남는다(웹 로그인으로 대체되어 동작 자체는 정상)
      throughTalk: false,
      success: function(response) {
        // 프로필 조회는 서버가 같은 토큰으로 직접 한다 — 여기서 받아봐야 신뢰할 수 없는 값이다
        sendUserInfoToBackend(response.access_token);
      },
      fail: function(error) {
        console.error('카카오 로그인 실패:', error);
        setError('로그인에 실패했습니다.');
        setIsLoading(false);
      }
    });
  };

  // 개발 모드 전용 테스트 로그인
  const handleTestLogin = async () => {
    if (isLoading) return;
    if (!testNickname.trim()) {
      setError('테스트 닉네임을 입력하세요.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/test-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname: testNickname.trim() })
      });
      if (response.ok) {
        const res = await response.json();
        handleLoginSuccess(res.data);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.errorMessage || errorData.message || '테스트 로그인 실패');
      }
    } catch (error) {
      console.error('테스트 로그인 오류:', error);
      setError('테스트 로그인 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16161d 100%)'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '16px',
        textAlign: 'center',
        maxWidth: '400px',
        width: '100%'
      }}>
        <h1 style={{ fontSize: '3rem', marginBottom: '8px' }}>ReUs</h1>
        <p style={{ color: '#666', marginBottom: '40px' }}>그날의 우리</p>
        
        {/* 에러 메시지 표시 */}
        {error && (
          <div style={{
            background: '#f8d7da',
            color: '#721c24',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}
        
        <button
          style={{
            width: '100%',
            backgroundColor: isLoading ? '#ccc' : '#FEE500',
            color: '#000',
            border: 'none',
            padding: '16px 20px',
            borderRadius: '12px',
            fontSize: '1.1rem',
            fontWeight: '600',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.7 : 1
          }}
          onClick={handleKakaoLogin}
          disabled={!isKakaoLoaded || isLoading}
        >
          {isLoading ? '로그인 중...' : (isKakaoLoaded ? '카카오로 시작하기' : '로딩 중...')}
        </button>

        {process.env.NODE_ENV === 'development' && (
          <div style={{
            marginTop: '24px',
            paddingTop: '20px',
            borderTop: '1px dashed #ccc',
            textAlign: 'left'
          }}>
            <p style={{ color: '#999', fontSize: '0.8rem', marginBottom: '8px' }}>
              🛠 개발용 테스트 로그인 (프로덕션 빌드에는 없음)
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                value={testNickname}
                onChange={(e) => setTestNickname(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleTestLogin(); }}
                placeholder="닉네임 (1~10자)"
                style={{
                  flex: 1,
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '0.9rem'
                }}
              />
              <button
                onClick={handleTestLogin}
                disabled={isLoading}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#667eea',
                  color: 'white',
                  fontWeight: '600',
                  cursor: isLoading ? 'not-allowed' : 'pointer'
                }}
              >
                로그인
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KakaoLogin;
